import { assertInArray, assertNumber, assertObject } from '../validators';
import { Config } from '../video-data/config';
import { clamp } from './other';

export const TOLERANCE = 1e-5;

export type Anchor = typeof Config.START_ANIMATION_CURVE_ANCHOR | typeof Config.MIDDLE_ANIMATION_CURVE_ANCHOR | typeof Config.END_ANIMATION_CURVE_ANCHOR;

export type RemoveAnchoredPoints = Anchor | typeof Config.NONE_ANIMATION_CURVE_ANCHOR;

type TangentDeltas = {
    in?: {
        x: number;
        y: number;
    };
    out?: {
        x: number;
        y: number;
    };
};

export type Position = {
    x: number;
    y: number;
};

export type CurvePointDescription = {
    anchor?: Anchor;
    position: Position;
};

type Bounds = {
    smallestX: number;
    largestX: number;
};

abstract class Point {
    abstract readonly classType: string;

    _x = 0;
    _y = 0;

    // start, middle or end - used during scaling
    _anchor: Anchor = Config.MIDDLE_ANIMATION_CURVE_ANCHOR;

    constructor(pos: Position) {
        this._x = pos.x || 0;
        this._y = pos.y || 0;
    }

    get anchor() {
        return this._anchor;
    }

    set anchor(value: Anchor) {
        assertInArray(value, [
            Config.START_ANIMATION_CURVE_ANCHOR,
            Config.MIDDLE_ANIMATION_CURVE_ANCHOR,
            Config.END_ANIMATION_CURVE_ANCHOR,
        ]);
        this._anchor = value;
    }

    set x(value: number) {
        assertNumber(value);
        this._x = value;
    }

    get x(): number {
        return this._x;
    }

    set y(value: number) {
        assertNumber(value);
        this._y = value;
    }

    get y(): number {
        return this._y;
    }

    setDelta(x: number, y: number) {
        if (typeof x === 'string') {
            x = parseFloat(x);
        }
        assertNumber(x);
        if (typeof y === 'string') {
            y = parseFloat(y);
        }
        assertNumber(y);
        this.setPosition(this.x + x, this.y + y);
    }

    setPosition(x: number, y: number) {
        // TODO: provide a consistent interface to position
        // currently we mix between x, y and {x: 1, y: 1}
        if (typeof x === 'string') {
            x = parseFloat(x);
        }
        if (typeof y === 'string') {
            y = parseFloat(y);
        }
        this.x = x;
        this.y = y;
    }

    getPosition(precision = 5): Position {
        return {
            x: parseFloat(this.x.toFixed(precision)),
            y: parseFloat(this.y.toFixed(precision)),
        };
    }

    clear() {
        this._x = 0;
        this._y = 0;
        this._anchor = Config.MIDDLE_ANIMATION_CURVE_ANCHOR;
    }

    destroy() {
        this.clear();
    }
}

export class ControlPoint extends Point {
    override readonly classType = 'ControlPoint';
    endPoint: EndPoint = null;

    override clear() {
        this.endPoint = null;
        super.clear();
    }
}

export class EndPoint extends Point {
    static isEndPoint(point: Point): point is EndPoint {
        return point.classType === 'EndPoint';
    }

    override readonly classType = 'EndPoint';
    _ip: Point = null;
    _op: Point = null;

    override get anchor(): Anchor {
        return this._anchor;
    }

    override set anchor(value: Anchor) {
        super.anchor = value;
        if (this.hasIn()) {
            this.inPoint.anchor = value;
        }
        if (this.hasOut()) {
            this.outPoint.anchor = value;
        }
    }

    hasOut() {
        return !!(this._op);
    }

    set outPoint(cp: Point) {
        this._op = cp;
    }

    get outPoint(): Point {
        return this._op;
    }

    hasIn() {
        return !!(this._ip);
    }

    set inPoint(cp: Point) {
        this._ip = cp;
    }

    get inPoint(): Point {
        return this._ip;
    }

    getTangentDeltas(): TangentDeltas {
        const deltas: TangentDeltas = {};
        if (this.hasIn()) {
            deltas.in = {
                x: (this.x - this.inPoint.x),
                y: (this.y - this.inPoint.y),
            };
        }
        if (this.hasOut()) {
            deltas.out = {
                x: (this.outPoint.x - this.x),
                y: (this.outPoint.y - this.y),
            };
        }
        return deltas;
    }

    setTangentDeltas(tangentDeltas: TangentDeltas, mirror = false) {
        const multi = (mirror) ? -1 : 1;

        if (this.hasIn()) {
            if (mirror && tangentDeltas.hasOwnProperty('out')) {
                this.inPoint.setPosition(
                    this.x + multi * tangentDeltas.out.x,
                    this.y + tangentDeltas.out.y
                );
            }
            else if (tangentDeltas.hasOwnProperty('in')) {
                this.inPoint.setPosition(
                    this.x + -1 * multi * tangentDeltas.in.x,
                    this.y + -1 * tangentDeltas.in.y
                );
            }
        }
        if (this.hasOut()) {
            if (mirror && tangentDeltas.hasOwnProperty('in')) {
                this.outPoint.setPosition(
                    this.x + multi * -1 * tangentDeltas.in.x,
                    this.y + multi * tangentDeltas.in.y
                );
            }
            else if (tangentDeltas.hasOwnProperty('out')) {
                this.outPoint.setPosition(
                    this.x + multi * tangentDeltas.out.x,
                    this.y + tangentDeltas.out.y
                );
            }
        }
    }

    scaleInTangent(value: number) {
        if (this.hasIn()) {
            this.inPoint.x = this.x - ((this.x - this.inPoint.x) * value);
        }
    }

    scaleOutTangent(value: number) {
        if (this.hasOut()) {
            this.outPoint.x = this.x + ((this.outPoint.x - this.x) * value);
        }
    }

    scaleTangents(value: number) {
        this.scaleInTangent(value);
        this.scaleOutTangent(value);
    }

    override setPosition(x: number, y: number, tangents = false) {
        const deltaX = x - this.x;
        const deltaY = y - this.y;
        super.setPosition(x, y);
        if (tangents) {
            if (this.hasIn()) {
                this._ip.setDelta(deltaX, deltaY);
            }
            if (this.hasOut()) {
                this._op.setDelta(deltaX, deltaY);
            }
        }
    }

    override setDelta(x: number, y: number, tangents = false) {
        super.setDelta(x, y);
        if (tangents) {
            if (this.hasIn()) {
                this._ip.setDelta(x, y);
            }
            if (this.hasOut()) {
                this._op.setDelta(x, y);
            }
        }
    }

    destroyIn() {
        if (this.hasIn()) {
            this.inPoint.destroy();
            this._ip = null;
        }
    }

    destroyOut() {
        if (this.hasOut()) {
            this.outPoint.destroy();
            this._op = null;
        }
    }

    override destroy(tangents = false) {
        if (tangents) {
            this.destroyIn();
            this.destroyOut();
        }
        else {
            this._ip = null;
            this._op = null;
        }
        super.destroy();
    }
}

export abstract class Curve {
    name = 'curve_1';
    _percentage = 0;
    percentageOffset = 0;
    _value = 0;
    _duration = 3.4;
    _points: (ControlPoint | EndPoint)[] = [];

    abstract _evalPercentage(value: number);

    set percentage(value: number) {
        this._percentage = clamp(value + this.percentageOffset, 0, 1);
        this._value = this._evalPercentage(this._percentage);
    }

    get percentage(): number {
        return this._percentage;
    }

    time2Percentage(time: number) {
        return time / this._duration;
    }

    get value(): number {
        return this._value;
    }

    get duration(): number {
        return this._duration;
    }

    set duration(value: number) {
        if (this._duration !== value) {
            const multi = this._duration / value;
            if (multi < 1.0) {
                this.scale(multi);
            }
            this._duration = value;
        }
    }

    get startDuration(): number {
        if (!this.hasBoundaryAnchors()) {
            return 0;
        }
        const points = this.getPoints('start');
        const npoints = points.length;
        if (npoints > 0) {
            return Math.abs(points[npoints - 1].x - points[0].x);
        }
        return 0;
    }

    get endDuration(): number {
        if (!this.hasBoundaryAnchors()) {
            return 0;
        }
        const points = this.getPoints(Config.END_ANIMATION_CURVE_ANCHOR);
        const npoints = points.length;
        if (npoints > 0) {
            return Math.abs(points[npoints - 1].x - points[0].x);
        }
        return 0;
    }

    hasBoundaryAnchors() {
        let hasStartAnchor = false;
        let hasEndAnchor = false;
        this.getPoints().forEach((p) => {
            if (p.anchor === Config.START_ANIMATION_CURVE_ANCHOR) {
                hasStartAnchor = true;
            }
            else if (p.anchor === Config.END_ANIMATION_CURVE_ANCHOR) {
                hasEndAnchor = true;
            }
        });
        return hasStartAnchor || hasEndAnchor;
    }

    assignBoundaryAnchors() {
        // automatically defines start and end
        const endPoints = this.getEndPoints();
        const nEndPoints = endPoints.length;

        // by using Math.floor we leave the "middle" points set to their default value of "middle"
        for (let i = 0; i < Math.floor(nEndPoints / 2); i++) {
            endPoints[i].anchor = Config.START_ANIMATION_CURVE_ANCHOR;
            endPoints[nEndPoints - (i + 1)].anchor = Config.END_ANIMATION_CURVE_ANCHOR;
        }
    }

    get points(): (ControlPoint | EndPoint)[] {
        return this._points;
    }

    getPoints(anchor: Anchor | typeof Config.ALL_ANIMATION_CURVE_ANCHOR = Config.ALL_ANIMATION_CURVE_ANCHOR) {
        if (anchor === Config.ALL_ANIMATION_CURVE_ANCHOR) {
            return this._points;
        }
        return this._points.filter((p) => p.anchor === anchor);
    }

    getPositions(): Position[] {
        return this._points.map((p) => p.getPosition());
    }

    setPositions(positions: Position[]) {
        if (this._points.length !== positions.length) {
            throw Error(`Data invalid. We should have ${this._points.length} positions instead of ${positions.length}`);
        }
        positions.forEach((p, i) => {
            this._points[i].setPosition(p.x, p.y);
        });
    }

    numberOfPoints(): number {
        return this._points.length;
    }

    clearPoints() {
        this._points = [];
    }

    /**
     * Get the largest x value for the given points
     * @param anchors: can be start, inbetween, end or all
     * @param pointType: can be end or all
     */
    largestX(anchors = Config.ALL_ANIMATION_CURVE_ANCHOR, pointType = 'end'): number {
        const points = (pointType === 'end') ? this.getEndPoints(anchors) : this.getPoints(anchors);
        if (points.length === 0) {
            return undefined;
        }
        return points[points.length - 1].x;
    }

    /**
     * Get the smallest x value for the given points
     * @param anchors: can be start, inbetween, end or all
     * @param pointType: can be end or all
     */
    smallestX(anchors = Config.ALL_ANIMATION_CURVE_ANCHOR, pointType = 'end'): number {
        const points = (pointType === 'end') ? this.getEndPoints(anchors) : this.getPoints(anchors);
        if (points.length === 0) {
            return undefined;
        }
        return points[0].x;
    }

    getEndPoints(anchor = Config.ALL_ANIMATION_CURVE_ANCHOR): EndPoint[] {
        // anchor all = start, inbetween and end
        const endPoints = this._points.filter(EndPoint.isEndPoint);

        if (anchor === Config.ALL_ANIMATION_CURVE_ANCHOR) {
            return endPoints;
        }

        return endPoints.filter((p) => p.anchor === anchor);
    }

    getEndPointPosition(index: number) {
        return this.getEndPoints()[index].getPosition();
    }

    setCurveCoordinates(_coordinatesString) {
    }

    abstract getCurveCoordinates(precision?: number);

    abstract getCurveDescription(precision?: number);

    setCurveDescription(_data) {
    }

    /**
     * If the first point is not at x === 0, adds a point at x = 0. The end result is a flat 'delay' line segment
     * leading up to the original first point.
     */
    addStartPoint() {
        if (this.points.length === 0) {
            return;
        }

        const firstPoint = this.points[0];

        if (firstPoint.x === 0) {
            return;
        }

        const insertPoint = this._createEndPoint({ x: 0, y: firstPoint.y });
        insertPoint.anchor = Config.START_ANIMATION_CURVE_ANCHOR;

        this.points.splice(0, 0, insertPoint);
    }

    /**
     * If the last point is not at x === 1, adds a point at x = 1. The end result is a flat 'delay' line segment
     * trailing out of the original last point.
     */
    addEndPoint() {
        if (this.points.length === 0) {
            return;
        }

        const lastPoint = this.points[this.points.length - 1];

        if (lastPoint.x === 1) {
            return;
        }

        const addedPoint = this._createEndPoint({ x: 1, y: lastPoint.y });
        addedPoint.anchor = Config.END_ANIMATION_CURVE_ANCHOR;

        this.points.push(addedPoint);
    }

    removePointsWithAnchor(anchor: Anchor) {
        const nPoints = this._points.length;
        for (let i = 0; i < nPoints; i++) {
            if (this._points[i].anchor === anchor) {
                this._points[i].destroy();
                this._points[i] = null;
            }
        }
        this._points = this._points.filter(point => point !== null);

        // cleanup extra unusable control point
        if (anchor === Config.START_ANIMATION_CURVE_ANCHOR || anchor === Config.END_ANIMATION_CURVE_ANCHOR) {
            const index = (anchor === Config.START_ANIMATION_CURVE_ANCHOR) ? 0 : this._points.length - 1;
            const point = this.points[index];
            if (point.classType === 'ControlPoint') {
                point.endPoint.destroyOut();
                this.points.splice(index, 1);
            }
        }
    }

    _createEndPoint(pos: Position): EndPoint {
        return new EndPoint(pos);
    }

    _addEndPoint(pos: Position): EndPoint {
        const p = this._createEndPoint(pos);
        this._points.push(p);
        return p;
    }

    move(x: number, y: number, anchor = Config.ALL_ANIMATION_CURVE_ANCHOR) {
        const endPoints = this.getEndPoints(anchor);
        for (let i = 0; i < endPoints.length; i++) {
            endPoints[i].setDelta(x, y, true);
        }
    }

    scale(scaleFactor: number, tangents = true, anchors = 'all') {
        // we should always be scaling relative to the bounds
        // of the entire curve, which is why we have 'all' here
        const largestX = Math.max(this.largestX('all'), 1);
        const smallestX = Math.min(this.smallestX('all'), 0);
        const bounds: Bounds = {
            smallestX,
            largestX,
        };
        this.scaleUsingBounds(scaleFactor, bounds, tangents, anchors);
        return this;
    }

    scaleUsingBounds(scaleFactor: number, bounds: Bounds, tangents = true, anchors = Config.ALL_ANIMATION_CURVE_ANCHOR) {
        assertObject(bounds);
        assertNumber(bounds.smallestX);
        assertNumber(bounds.largestX);

        const points = this.getPoints(anchors);
        for (let i = 0; i < points.length; i++) {
            let px = points[i].x;
            if (points[i].anchor === Config.START_ANIMATION_CURVE_ANCHOR) {
                px = bounds.smallestX + ((px - bounds.smallestX) * scaleFactor);
            }
            else if (points[i].anchor === Config.END_ANIMATION_CURVE_ANCHOR) {
                px = bounds.largestX - ((bounds.largestX - px) * scaleFactor);
            }
            const deltaX = px - points[i].x;

            if (!tangents && this.points[i].classType === 'EndPoint') {
                // since we don't want to scale the tangent we let the
                // endPoint move them along with it's own translation
                points[i].setDelta(deltaX, 0, true);
            }
            else {
                points[i].setDelta(deltaX, 0);
            }
        }
        return this;
    }

    destroy() {
        this._percentage = 0;
        this.percentageOffset = 0;
        this._value = 0;
        this._duration = 3.4;
        this._points = [];
    }
}
